import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ONE_PAGE_SECTIONS } from 'constants/businessToolSections/onePage';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { TextInputControl } from 'components/_basic/Form/TextInput';
import { FormHeader } from 'components/_basic/FormHeader';
import { FormSectionCard } from 'components/_basic/FormSectionCard';
import { FORM_SECTION_CARD_VARIANT } from 'components/_basic/FormSectionCard/types';
import { useHandleRequiredForAIInputBlur } from 'components/BusinessToolForm/hooks/useHandleRequiredForAIInputBlur';
import {
  ABOUT_COMPANY_MISSION_NAMES,
  prefix as missionAndVisionPrefix,
} from 'components/BusinessToolForm/Sections/AboutCompany/MissionAndVision/utils/form';
import { GLOBAL_FIELD_NAMES, globalPrefix } from 'components/BusinessToolForm/Sections/Global/form';
import { ProjectStatus } from 'components/BusinessToolForm/Shared/ProjectStatus';

import { concatWithDot } from 'helpers/string';

export const AboutTheProject: React.FC = () => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  const canAccess = useCheckSubSectionAccess(ONE_PAGE_SECTIONS.ABOUT_THE_PROJECT);
  const handleRequiredForAIInputBlur = useHandleRequiredForAIInputBlur();

  return (
    <>
      <FormSectionCard variant={FORM_SECTION_CARD_VARIANT.LARGE}>
        <FormHeader
          locked={!canAccess}
          title={t('chapters.ABOUT_THE_PROJECT')}
          section={ONE_PAGE_SECTIONS.ABOUT_THE_PROJECT}
        />
      </FormSectionCard>
      <FormSectionCard title={t('overview.project.projectStatus')} disabled={!canAccess}>
        <ProjectStatus canAccess={canAccess} />
      </FormSectionCard>
      {/* 0.2.2 */}
      <FormSectionCard
        title={t('overview.project.goalsAndObjectivesIdea')}
        aiFieldName={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.MAIN_GOALS)}
        disabled={!canAccess}
      >
        <TextInputControl
          multiline
          control={control}
          id="onePage.aboutTheCompany.goalsAndObjectives"
          name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.MAIN_GOALS)}
          disabled={!canAccess}
          minRows={4}
          maxLength={1000}
        />
      </FormSectionCard>
      {/* 0.2.3 */}
      <FormSectionCard
        title={t('overview.project.briefProjectDescription')}
        aiFieldName={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.PROJECT_BRIEF_DESCRIPTION)}
        aiOnlyUpdate
        disabled={!canAccess}
        requiredForAI
      >
        <TextInputControl
          multiline
          control={control}
          id="onePage.aboutTheCompany.briefProjectDescription"
          name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.PROJECT_BRIEF_DESCRIPTION)}
          disabled={!canAccess}
          minRows={4}
          maxLength={1000}
          onBlur={handleRequiredForAIInputBlur}
        />
      </FormSectionCard>
      <FormSectionCard
        disabled={!canAccess}
        title={t('company.mission.problemOfMarket')}
        aiFieldName={concatWithDot(missionAndVisionPrefix, ABOUT_COMPANY_MISSION_NAMES.PROBLEM_OF_MARKET)}
      >
        <TextInputControl
          multiline
          control={control}
          id="onePage.aboutTheCompany.problemOfMarket"
          name={concatWithDot(missionAndVisionPrefix, ABOUT_COMPANY_MISSION_NAMES.PROBLEM_OF_MARKET)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1000}
        />
      </FormSectionCard>
      {/* 0.2.5 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('company.mission.howProjectAddressesTheProblem')}
        aiFieldName={concatWithDot(
          missionAndVisionPrefix,
          ABOUT_COMPANY_MISSION_NAMES.HOW_PROJECT_ADDRESSES_THE_PROBLEM,
        )}
      >
        <TextInputControl
          multiline
          control={control}
          id="onePage.aboutTheCompany.howProjectAddressesTheProblem"
          name={concatWithDot(missionAndVisionPrefix, ABOUT_COMPANY_MISSION_NAMES.HOW_PROJECT_ADDRESSES_THE_PROBLEM)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1000}
        />
      </FormSectionCard>
    </>
  );
};
